/** Resumen de lo que no se pudo asignar en un lote de Grupos («Asignar»,
 * «Repartir automático»).
 *
 * Eduardo 2026-10-05, torneo 69: la bitácora tiene 9 rechazos seguidos al
 * mismo grupo (sin el texto del aviso). Con el aviso de antes, nueve
 * rechazos iguales se repetían nueve veces, uno por jugador. Ahora un mismo
 * motivo se dice una vez: «No se asignó ninguno de 9: el grupo «GRUPO A» es
 * de Categoria A.». */

export interface GroupAssignmentFailure {
  /** Nombre del jugador o pareja. */
  label: string;
  /** Aviso completo (el del servidor, o «error de red»). */
  message: string;
  /** Parte común a todo el lote, sin el nombre del jugador (`details.reason`
   * del servidor). Si viene, `message` ya trae el nombre. */
  reason?: string | null;
}

function withoutFinalPeriod(text: string): string {
  return text.trim().replace(/\.+$/, "");
}

/** Para empezar una oración con el aviso («error de red» → «Error de red»). */
function asSentence(text: string): string {
  const clean = withoutFinalPeriod(text);
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

export function summarizeGroupAssignmentFailures(
  total: number,
  failures: GroupAssignmentFailure[],
): string {
  // Uno solo: el aviso tal cual (si no trae el nombre, se dice a quién).
  if (total === 1 && failures.length === 1) {
    const [only] = failures;
    return only.reason
      ? `${asSentence(only.message)}.`
      : `No se pudo asignar a ${only.label}. ${asSentence(only.message)}.`;
  }
  const assigned = total - failures.length;
  const byReason = new Map<string, GroupAssignmentFailure[]>();
  for (const failure of failures) {
    const key = withoutFinalPeriod(failure.reason || failure.message);
    byReason.set(key, [...(byReason.get(key) ?? []), failure]);
  }
  const head = assigned === 0
    ? `No se asignó ninguno de ${total}`
    : `${assigned === 1 ? "Se asignó" : "Se asignaron"} ${assigned} de ${total}`;
  if (assigned === 0 && failures.length > 1 && byReason.size === 1) {
    const [reason] = Array.from(byReason.keys());
    // `reason` del servidor es un pedazo de oración («el grupo «X» es de…»);
    // sin él va el aviso completo, que es una oración aparte.
    return failures[0].reason ? `${head}: ${reason}.` : `${head}. ${asSentence(reason)}.`;
  }
  const parts = Array.from(byReason.entries()).map(([reason, items]) => {
    if (items.length > 1) return `${reason} (${items.map((item) => item.label).join(", ")})`;
    const [only] = items;
    return only.reason ? withoutFinalPeriod(only.message) : `${only.label}: ${withoutFinalPeriod(only.message)}`;
  });
  return `${head}. Fallaron: ${parts.join(" · ")}.`;
}
